import { useEffect } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { bindQueryCacheToActiveClient } from "@/lib/client-scope-cache";
import { useAuthStore } from "@/stores/auth";
import { useClientSwitcherStore } from "@/stores/client-switcher.store";
import { useCurrentUserProfile } from "@/hooks/useUsers";
import { FEATURE_KEYS, type FeatureKey } from "@school-sales-ai/shared";
import { useClientFeatures } from "@/hooks/useClientFeatures";
import { Layout } from "@/components/Layout";
import { Login } from "@/pages/Login";
import { LeadDetail } from "@/pages/LeadDetail";
import { Inbox } from "@/pages/Inbox";
import { LeadsPipeline } from "@/pages/LeadsPipeline";
import { Visits } from "@/pages/Visits";
import { Plantillas } from "@/pages/Plantillas";
import { Promociones } from "@/pages/Promociones";
import { Notificaciones } from "@/pages/Notificaciones";
import { SitioWeb } from "@/pages/SitioWeb";
import { EnviosMasivos } from "@/pages/EnviosMasivos";
import { Pedidos } from "@/pages/Pedidos";
import { Cotizaciones } from "@/pages/Cotizaciones";
import { CotizacionDetalle } from "@/pages/CotizacionDetalle";
import { PedidoDetalle } from "@/pages/PedidoDetalle";
import { EnvioDetalle } from "@/pages/EnvioDetalle";
import { Reports } from "@/pages/Reports";
import { Agent } from "@/pages/Agent";
import { Catalogo } from "@/pages/Catalogo";
import { Users } from "@/pages/Users";
import { WhatsAppConnect } from "@/pages/WhatsAppConnect";
import { InstagramConnect } from "@/pages/InstagramConnect";
import { Facturacion } from "@/pages/Facturacion";
import { AuthCallback } from "@/pages/AuthCallback";
import { AvisoDePrivacidad } from "@/pages/AvisoDePrivacidad";
import { TerminosYCondiciones } from "@/pages/TerminosYCondiciones";
import { TermsGate } from "@/components/TermsGate";
import { ClientsList } from "@/pages/super-admin/ClientsList";
import { ScriptsList } from "@/pages/super-admin/ScriptsList";
import { ScriptEditor } from "@/pages/super-admin/ScriptEditor";
import { DeploymentEditor } from "@/pages/super-admin/DeploymentEditor";
import { UsageDashboard } from "@/pages/super-admin/UsageDashboard";
import { PlatformUsers } from "@/pages/super-admin/PlatformUsers";
import { PlansList } from "@/pages/super-admin/PlansList";
import { Billing } from "@/pages/super-admin/Billing";
import { OnboardingWizard } from "@/pages/super-admin/onboarding/OnboardingWizard";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

function AuthProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const setSession = useAuthStore((s) => s.setSession);
  const setInitialized = useAuthStore((s) => s.setInitialized);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setInitialized();
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, [setSession, setInitialized]);

  return <>{children}</>;
}

function AdminRoute(): React.JSX.Element {
  const session = useAuthStore((s) => s.session);
  const profile = useCurrentUserProfile();

  if (session && !profile) {
    return (
      <div className="flex h-screen items-center justify-center bg-ink-050">
        <span className="text-sm text-ink-500">Cargando…</span>
      </div>
    );
  }

  // Account managers get admin capabilities over their selected school; the
  // backend separately confirms the X-Client-Id is one they're assigned to.
  if (
    profile?.role !== "admin" &&
    profile?.role !== "super_admin" &&
    profile?.role !== "account_manager"
  ) {
    return <Navigate to="/pipeline" replace />;
  }

  return <Outlet />;
}

// Who may provision a client: super-admins and account managers, the CRM mirror of
// the backend's `authorizeOnboarder`. An account manager only ever reaches their own
// schools — the wizard's routes are addressed by client id and the backend rejects a
// school outside their portfolio, so this guard is about the role, not the row.
function OnboarderRoute(): React.JSX.Element {
  const session = useAuthStore((s) => s.session);
  const profile = useCurrentUserProfile();

  if (session && !profile) {
    return (
      <div className="flex h-screen items-center justify-center bg-ink-050">
        <span className="text-sm text-ink-500">Cargando…</span>
      </div>
    );
  }

  if (profile?.role !== "super_admin" && profile?.role !== "account_manager") {
    return <Navigate to="/pipeline" replace />;
  }

  return <Outlet />;
}

function SuperAdminRoute(): React.JSX.Element {
  const session = useAuthStore((s) => s.session);
  const profile = useCurrentUserProfile();

  if (session && !profile) {
    return (
      <div className="flex h-screen items-center justify-center bg-ink-050">
        <span className="text-sm text-ink-500">Cargando…</span>
      </div>
    );
  }

  if (profile?.role !== "super_admin") {
    return <Navigate to="/pipeline" replace />;
  }

  return <Outlet />;
}

// The CRM home is role-aware: account managers land on their portfolio of
// schools (they must pick one before any client-scoped page works); everyone
// else lands on the pipeline.
function HomeRedirect(): React.JSX.Element {
  const session = useAuthStore((s) => s.session);
  const profile = useCurrentUserProfile();

  if (session && !profile) {
    return (
      <div className="flex h-screen items-center justify-center bg-ink-050">
        <span className="text-sm text-ink-500">Cargando…</span>
      </div>
    );
  }

  if (profile?.role === "account_manager") {
    return <Navigate to="/clients" replace />;
  }
  return <Navigate to="/pipeline" replace />;
}

function AccountManagerRoute(): React.JSX.Element {
  const session = useAuthStore((s) => s.session);
  const profile = useCurrentUserProfile();

  if (session && !profile) {
    return (
      <div className="flex h-screen items-center justify-center bg-ink-050">
        <span className="text-sm text-ink-500">Cargando…</span>
      </div>
    );
  }

  if (profile?.role !== "account_manager") {
    return <Navigate to="/pipeline" replace />;
  }

  return <Outlet />;
}

/**
 * Redirects away from a module the active client's plan does not grant.
 *
 * The backend 403s independently — this is UX polish so a deep link or a stale
 * nav item doesn't land on a dead page. **Waiting for `isPending` is the part
 * that matters**: without it the guard reads an empty feature list before the
 * answer is known and bounces a legitimate deep link.
 *
 * One component rather than one per module: there were three byte-identical
 * copies, and a fix to the pending branch had to be remembered three times.
 */
function FeatureGuard({ feature }: { feature: FeatureKey }): React.JSX.Element {
  const { data: features, isPending } = useClientFeatures();

  if (isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-ink-050">
        <span className="text-sm text-ink-500">Cargando…</span>
      </div>
    );
  }

  if (!(features ?? []).includes(feature)) {
    return <Navigate to="/pipeline" replace />;
  }

  return <Outlet />;
}

// Account managers are platform users with no owning client, so a client-scoped
// page 403s until they select a school. Send them to their portfolio to pick one.
// Other roles pass through untouched.
function ClientScopeGuard(): React.JSX.Element {
  const profile = useCurrentUserProfile();
  const activeClientId = useClientSwitcherStore((s) => s.activeClientId);

  if (profile?.role === "account_manager" && !activeClientId) {
    return <Navigate to="/clients" replace />;
  }

  return <Outlet />;
}

function ProtectedRoute(): React.JSX.Element {
  const initialized = useAuthStore((s) => s.initialized);
  const session = useAuthStore((s) => s.session);

  if (!initialized) {
    return (
      <div className="flex h-screen items-center justify-center bg-ink-050">
        <span className="text-sm text-ink-500">Cargando…</span>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}


const router = createBrowserRouter([
  { path: "/login", element: <Login /> },
  { path: "/auth/callback", element: <AuthCallback /> },
  { path: "/aviso-de-privacidad", element: <AvisoDePrivacidad /> },
  { path: "/terminos-y-condiciones", element: <TerminosYCondiciones /> },
  {
    element: <ProtectedRoute />,
    children: [
      {
        // Every authenticated screen sits under this gate — including the
        // onboarding wizard, which lives outside Layout — until the user accepts
        // the current Términos y condiciones. See components/TermsGate.tsx.
        element: <TermsGate />,
        children: [
          {
            // Outside Layout on purpose: the wizard is full screen so the client
            // switcher cannot be moved mid-flow. See OnboardingWizard's tenant gate.
            element: <OnboarderRoute />,
            children: [
              {
                path: "/super-admin/onboarding/:clientId",
                element: <OnboardingWizard />,
              },
            ],
          },
          {
            element: <Layout />,
            children: [
              { index: true, element: <HomeRedirect /> },
              {
                // The account manager's portfolio of assigned schools (the scoped
                // Clientes screen). Reachable before any school is selected.
                element: <AccountManagerRoute />,
                children: [{ path: "/clients", element: <ClientsList /> }],
              },
              {
                // Client-scoped pages — an account manager must have a school
                // selected first, otherwise ClientScopeGuard sends them to /clients.
                element: <ClientScopeGuard />,
                children: [
                  // The lead table and the Kanban merged into one page; /leads is
                  // kept as a redirect so old links and bookmarks still land.
                  { path: "/leads", element: <Navigate to="/pipeline" replace /> },
                  { path: "/leads/:id", element: <LeadDetail /> },
                  { path: "/buzon", element: <Inbox /> },
          { path: "/pipeline", element: <LeadsPipeline /> },
                  {
                    element: <FeatureGuard feature={FEATURE_KEYS.visits} />,
                    children: [{ path: "/visitas", element: <Visits /> }],
                  },
                  {
                    // Not gated on ORDERS_ENABLED: that flag stops the AGENT from
                    // taking new orders, while staff must still be able to close the
                    // ones that already exist. Same split visits.routes.ts makes.
                    element: <FeatureGuard feature={FEATURE_KEYS.orders} />,
                    children: [
                      { path: "/pedidos", element: <Pedidos /> },
                      { path: "/pedidos/:id", element: <PedidoDetalle /> },
                    ],
                  },
                  {
                    // Not gated on QUOTES_ENABLED, for the orders reason: staff work
                    // the requests that already exist while the agent side is off.
                    element: <FeatureGuard feature={FEATURE_KEYS.quotes} />,
                    children: [
                      { path: "/cotizaciones", element: <Cotizaciones /> },
                      { path: "/cotizaciones/:id", element: <CotizacionDetalle /> },
                    ],
                  },
                  {
                    // Envíos masivos rides the SAME feature as Plantillas — broadcasts
                    // are part of that module, not a separate SKU. Deliberately NOT
                    // gated on the school's own authorization switch: revoking it
                    // must not erase the record of what was already sent and billed.
                    element: <FeatureGuard feature={FEATURE_KEYS.whatsappTemplates} />,
                    children: [
                      { path: "/envios", element: <EnviosMasivos /> },
                      { path: "/envios/:id", element: <EnvioDetalle /> },
                    ],
                  },
                  { path: "/reports", element: <Reports /> },
                  // Same audience as Agente, where it used to be a tab.
                  { path: "/catalogo", element: <Catalogo /> },
                  { path: "/agent", element: <Agent /> },
                  {
                    element: <AdminRoute />,
                    children: [
                      { path: "/users", element: <Users /> },
                      { path: "/whatsapp-connect", element: <WhatsAppConnect /> },
                      {
                        element: <FeatureGuard feature={FEATURE_KEYS.instagram} />,
                        children: [{ path: "/instagram-connect", element: <InstagramConnect /> }],
                      },
                      {
                        element: <FeatureGuard feature={FEATURE_KEYS.whatsappTemplates} />,
                        children: [{ path: "/plantillas", element: <Plantillas /> }],
                      },
                      // Admin-only end to end, reads included: a promotion IS the
                      // price list, and the whole point of the silent mode is that
                      // nobody reading the CRM is told a rule produced the price.
                      {
                        element: <FeatureGuard feature={FEATURE_KEYS.promotions} />,
                        children: [{ path: "/promociones", element: <Promociones /> }],
                      },
                      // Admin-only: these switches decide who on the team is told
                      // what. The server enforces the same rule on reads too.
                      {
                        element: <FeatureGuard feature={FEATURE_KEYS.webNotifications} />,
                        children: [{ path: "/notificaciones", element: <Notificaciones /> }],
                      },
                      // Admin-only, reads included: the draft is unpublished work
                      // (site.routes.ts enforces the same).
                      {
                        element: <FeatureGuard feature={FEATURE_KEYS.website} />,
                        children: [{ path: "/sitio-web", element: <SitioWeb /> }],
                      },
                      // Admin-only: what the school pays is not admissions staff's
                      // business. The server enforces the same rule.
                      { path: "/facturacion", element: <Facturacion /> },
                    ],
                  },
                ],
              },
              {
                element: <SuperAdminRoute />,
                children: [
                  { path: "/super-admin/clients", element: <ClientsList /> },
                  { path: "/super-admin/plans", element: <PlansList /> },
                  { path: "/super-admin/platform-users", element: <PlatformUsers /> },
                  { path: "/super-admin/scripts", element: <ScriptsList /> },
                  { path: "/super-admin/scripts/:id", element: <ScriptEditor /> },
                  { path: "/super-admin/usage", element: <UsageDashboard /> },
                  { path: "/super-admin/billing", element: <Billing /> },
                ],
              },
              {
                // Step 5 of the wizard is done here, so whoever may onboard a client
                // may publish its first deployment. Scoped server-side to the caller's
                // own schools.
                element: <OnboarderRoute />,
                children: [
                  {
                    path: "/super-admin/clients/:clientId/deployment",
                    element: <DeploymentEditor />,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]);

export function App(): React.JSX.Element {
  // A client switch drops the previous client's cached data (see the module).
  useEffect(() => bindQueryCacheToActiveClient(queryClient), []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </QueryClientProvider>
  );
}
